import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// AI 分析结果接口
interface AIAnalysisResult {
	keywords : string[]
	locations ?: {
		explicit : string[]
		implied : string[]
	}
	difficulty : {
		score : number
		reason : string
	}
	timeCommitment ?: {
		weeklyHours : number
		durationWeeks : number
		description : string
	}
	categories : Array<{
		name : string
		confidence : number
	}>
	sentiment : {
		score : number
		primaryEmotion : string
		emotions : string[]
	}
	feasibility : {
		level : 'high' | 'medium' | 'low'
		factors : string[]
	}
	summary : string
}

// 愿望草稿接口
interface WishDraft {
	id ?: string
	nickname ?: string
	description : string
	picUrl ?: string
	categoryId ?: number
	keywords ?: string[]
	difficulty ?: number
	sentimentScore ?: number
	targetDate ?: string
	capsuleOpenDate ?: string
	isTimeCapsule : boolean
	isPublic : boolean
	isAiProcessed ?: AIAnalysisResult
}

// 愿望状态枚举
export enum WishStatus {
	DRAFT = 'draft',
	PUBLISHED = 'published',
	GROWING = 'growing',
	LAUNCHED = 'launched',
	ACHIEVED = 'achieved',
	ARCHIVED = 'archived'
}

// 愿望成长阶段
export enum GrowthStage {
	SEED = 'seed',           // 种子期 (1天)
	SPROUT = 'sprout',       // 萌芽期 (7天)
	GROWING = 'growing',     // 成长期 (30天)
	BLOOMING = 'blooming',   // 开花期 (60天)
	FRUITING = 'fruiting'    // 结果期 (90天)
}

// 默认草稿
const defaultDraft : WishDraft = {
	description: '',
	picUrl: '',
	isPublic: true,
	targetDate: undefined,
	isTimeCapsule: false,
	capsuleOpenDate: undefined
}

export const useWishStore = defineStore('wish', () => {
	// State
	const draft = ref<WishDraft>({ ...defaultDraft })
	const analyzing = ref(false)
	const analysisProgress = ref(0)
	const currentWish = ref<any>(null)
	const myWishes = ref<any[]>([])
	const loading = ref(false)

	// Getters
	const canPublish = computed(() => {
		return draft.value.content.length >= 10 && !analyzing.value
	})

	const wordCount = computed(() => {
		return draft.value.content.length
	})

	const growthProgress = computed(() => {
		if (!currentWish.value) return 0

		const createdAt = new Date(currentWish.value.createTime).getTime()
		const now = Date.now()
		const days = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24))

		// 基础进度：基于天数线性增长
		let progress = Math.min(days / 90 * 100, 100)

		// 守护人数加速（每个守护+2%）
		const guardCount = currentWish.value.guardCount || 0
		progress = Math.min(progress + guardCount * 2, 100)

		return Math.round(progress)
	})

	const growthStage = computed(() => {
		const progress = growthProgress.value
		if (progress < 10) return GrowthStage.SEED
		if (progress < 30) return GrowthStage.SPROUT
		if (progress < 60) return GrowthStage.GROWING
		if (progress < 90) return GrowthStage.BLOOMING
		return GrowthStage.FRUITING
	})

	const stageText = computed(() => {
		const stageMap : Record<string, string> = {
			[GrowthStage.SEED]: '种子期',
			[GrowthStage.SPROUT]: '萌芽期',
			[GrowthStage.GROWING]: '成长期',
			[GrowthStage.BLOOMING]: '开花期',
			[GrowthStage.FRUITING]: '结果期'
		}
		return stageMap[growthStage.value] || '未知'
	})

	// Actions
	const setDraft = (data : Partial<WishDraft>) => {
		draft.value = { ...draft.value, ...data }
		// 自动保存到本地
		uni.setStorageSync('wishDraft', draft.value)
	}

	const loadDraft = () => {
		const saved = uni.getStorageSync('wishDraft') as WishDraft | null
		if (saved) {
			draft.value = saved
		}
	}

	const clearDraft = () => {
		draft.value = { ...defaultDraft }
		uni.removeStorageSync('wishDraft')
	}

	const analyzeContent = async (content : string) : Promise<AIAnalysisResult | null> => {
		if (content.length < 10) return null

		analyzing.value = true
		analysisProgress.value = 0

		try {
			// 模拟进度条
			const progressTimer = setInterval(() => {
				if (analysisProgress.value < 90) {
					analysisProgress.value += 10
				}
			}, 200)

			const res = await uni.request({
				url: 'https://your-api-domain.com/api/ai/analyze',
				method: 'POST',
				data: { content }
			})

			clearInterval(progressTimer)
			analysisProgress.value = 100

			const data = res.data as any
			if (data.code === 200) {
				draft.value.aiAnalysis = data.data
				return data.data
			}
			return null
		} catch (e) {
			console.error('AI分析失败:', e)
			uni.showToast({ title: '分析失败，请重试', icon: 'none' })
			return null
		} finally {
			setTimeout(() => {
				analyzing.value = false
				analysisProgress.value = 0
			}, 500)
		}
	}

	const publishWish = async () : Promise<boolean> => {
		if (!canPublish.value) return false

		loading.value = true
		try {
			const token = uni.getStorageSync('token') as string
			const res = await uni.request({
				url: 'https://your-api-domain.com/api/wish',
				method: 'POST',
				header: {
					'Authorization': `Bearer ${token}`
				},
				data: draft.value
			})

			const data = res.data as any
			if (data.code === 200) {
				uni.showToast({ title: '发布成功', icon: 'success' })
				clearDraft()
				return true
			} else {
				// 处理每日发布限制
				if (data.code === 429) {
					uni.showModal({
						title: '发布次数已达上限',
						content: '今日发布次数已用完，观看广告可解锁额外次数',
						confirmText: '看广告',
						cancelText: '取消',
						success: (modalRes) => {
							if (modalRes.confirm) {
								// 调用广告接口
								watchAdToUnlock()
							}
						}
					})
				} else {
					uni.showToast({ title: data.message || '发布失败', icon: 'none' })
				}
				return false
			}
		} catch (e) {
			console.error('发布失败:', e)
			uni.showToast({ title: '发布失败', icon: 'none' })
			return false
		} finally {
			loading.value = false
		}
	}

	const watchAdToUnlock = () => {
		// #ifdef MP-WEIXIN
		// 微信小程序激励视频广告
		const rewardedVideoAd = uni.createRewardedVideoAd({
			adUnitId: 'your-ad-unit-id'
		})

		rewardedVideoAd.show().catch(() => {
			rewardedVideoAd.load().then(() => rewardedVideoAd.show())
		})

		rewardedVideoAd.onClose((res) => {
			if (res && res.isEnded) {
				// 广告播放完成，解锁发布
				uni.showToast({ title: '解锁成功', icon: 'success' })
			}
		})
		// #endif
	}

	const fetchMyWishes = async (status ?: string, page = 1, size = 10) => {
		loading.value = true
		try {
			const token = uni.getStorageSync('token') as string
			const res = await uni.request({
				url: 'https://your-api-domain.com/api/wish/list',
				method: 'GET',
				header: {
					'Authorization': `Bearer ${token}`
				},
				data: { status, page, size }
			})

			const data = res.data as any
			if (data.code === 200) {
				if (page === 1) {
					myWishes.value = data.data.list
				} else {
					myWishes.value.push(...data.data.list)
				}
				return data.data
			}
			return null
		} catch (e) {
			console.error('获取愿望列表失败:', e)
			return null
		} finally {
			loading.value = false
		}
	}

	const fetchWishDetail = async (id : string) => {
		try {
			const token = uni.getStorageSync('token') as string
			const res = await uni.request({
				url: `https://your-api-domain.com/api/wish/${id}`,
				method: 'GET',
				header: {
					'Authorization': `Bearer ${token}`
				}
			})

			const data = res.data as any
			if (data.code === 200) {
				currentWish.value = data.data
				return data.data
			}
			return null
		} catch (e) {
			console.error('获取愿望详情失败:', e)
			return null
		}
	}

	const likeWish = async (id : string) => {
		try {
			const token = uni.getStorageSync('token') as string
			await uni.request({
				url: `https://your-api-domain.com/api/wish/${id}/like`,
				method: 'POST',
				header: {
					'Authorization': `Bearer ${token}`
				}
			})
		} catch (e) {
			console.error('点赞失败:', e)
		}
	}

	const guardWish = async (id : string) => {
		try {
			const token = uni.getStorageSync('token') as string
			await uni.request({
				url: `https://your-api-domain.com/api/wish/${id}/guard`,
				method: 'POST',
				header: {
					'Authorization': `Bearer ${token}`
				}
			})
		} catch (e) {
			console.error('守护失败:', e)
		}
	}

	const launchWish = async (id : string) : Promise<boolean> => {
		try {
			const token = uni.getStorageSync('token') as string
			const res = await uni.request({
				url: `https://your-api-domain.com/api/wish/${id}/launch`,
				method: 'POST',
				header: {
					'Authorization': `Bearer ${token}`
				}
			})

			const data = res.data as any
			if (data.code === 200) {
				return true
			}
			return false
		} catch (e) {
			console.error('发射愿望失败:', e)
			return false
		}
	}

	return {
		draft,
		analyzing,
		analysisProgress,
		currentWish,
		myWishes,
		loading,
		canPublish,
		wordCount,
		growthProgress,
		growthStage,
		stageText,
		setDraft,
		loadDraft,
		clearDraft,
		analyzeContent,
		publishWish,
		fetchMyWishes,
		fetchWishDetail,
		likeWish,
		guardWish,
		launchWish
	}
})